/**
 * Daily and weekly quests. Bot source: `rpg/battlepass/quests.ts`
 * (`questGenerator`, `checkQuestsDone`, `checkQuestsAdvancement`,
 * `questReward`, `updateQuest`) and the counter increments spread over the
 * bot's systems (each web system emits events; `trackQuestEvents` moves the
 * same counters the bot moves).
 *
 * Bot rules (T3.3, P-11/P-12): the full pool, 5 + 3 premium quests per
 * period, premium quests locked below their tier, completed quests removed.
 * "Open N chests" / "Get N nuggets" never progress until the shared fix P-13
 * lands in both clients (T4.8).
 */
import { questDefs, type QuestDef } from '../data/index.js';
import type { GameEvent } from '../events.js';
import { teamPower } from '../formulas/power.js';
import { premiumOf, type PremiumBoost } from '../premium.js';
import { randInt, type Rng } from '../rng.js';
import type { BotUser, QuestPeriod } from '../state.js';
import { battlepassLevelFor } from './battlepass.js';

export type QuestCategory = 'daily' | 'weekly';
export const QUEST_CATEGORIES: readonly QuestCategory[] = ['daily', 'weekly'];

/** Quests drawn per period: 5 for everyone, the last 3 gated by premium tier 1–3. */
export const QUESTS_PER_PERIOD = 5 + 3;
/** `premiumAdjust`: target multiplier of a premium quest, by premium tier. */
const PREMIUM_ADJUST = [1, 2, 3, 4, 5];

export function questDef(category: QuestCategory, id: number): QuestDef | undefined {
  return questDefs[category].find((q) => q.id === id);
}

/** Daily quests end at the first midnight after they were drawn (`questGenerator`). */
export function dailyResetAt(save: BotUser): number {
  const d = new Date(save.stats.quest.daily.date);
  d.setDate(d.getDate() + 1);
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  return d.getTime();
}

/**
 * The bot's weekly boundary: next Monday 0:00 — or the 1st of the next
 * month when that Monday falls in it (parity K-10, kept).
 */
export function weeklyResetAfter(now: number): number {
  const next = new Date(now);
  next.setHours(0);
  next.setMinutes(0);
  next.setSeconds(0);
  const change = (1 + 7 - next.getDay()) % 7;
  next.setDate(next.getDate() + (change === 0 ? 7 : change));
  if (next.getMonth() > new Date(now).getMonth()) next.setDate(1);
  return next.getTime();
}

export function weeklyResetAt(save: BotUser): number {
  return save.stats.quest.weekly.date;
}

/** `utils/math.ts#randDistinctInt(0, max, n)` (a safety cap replaces the bot's unbounded loop). */
function randDistinct(rng: Rng, max: number, n: number): number[] {
  const picked: number[] = [];
  if (max < n) {
    for (let i = 0; i < n; i++) picked.push(randInt(rng, 0, max));
    return picked;
  }
  for (let tries = 0; picked.length < n && tries < 10_000; tries++) {
    const v = randInt(rng, 0, max);
    if (!picked.includes(v)) picked.push(v);
  }
  for (let v = 0; picked.length < n; v++) if (!picked.includes(v)) picked.push(v);
  return picked;
}

/** `createFreshTrackingPeriod`. */
export function freshPeriod(save: BotUser, date: number, premium: number): QuestPeriod {
  return {
    checked: false,
    date,
    money: Math.floor(save.money),
    lootbox: Math.floor(save.lootbox),
    level: save.level.level,
    premium,
    attack: teamPower(save),
    chafer: { count: 0, bet: 0 },
    bet: { count: 0, bet: 0 },
    mission: { count: 0, fakecoin: 0, lootcoin: 0 },
    toutou: { count: 0, toutou: 0, golden: 0, dark: 0, diamond: 0, king: 0 },
    boss: { count: 0, damage: 0 },
    reset: { count: 0 },
    weapon: { one: 0, two: 0, three: 0, four: 0, five: 0 },
    character: { one: 0, two: 0, three: 0, four: 0 },
    adventures: { normal: 0, hard: 0, nightmare: 0, kill: 0 },
    spot: { count: 0, bet: 0 },
    larva: { count: 0, bet: 0 },
    items: { total: 0, characters: 0, weapons: 0, shields: 0, pets: 0, legendary: 0 },
    upgrade: { total: 0, characters: 0, weapons: 0, shields: 0, pets: 0 },
    battlepass: { step: 0 },
    damage: { total: 0 },
    pvp: { total: 0 },
    chests: { total: 0 },
    spend: { fakecoin: 0, lootcoin: 0 },
    quests: { total: 0 },
    nuggets: { total: 0, used: 0 },
    raid: { total: 0 },
    reward: { total: 0 },
  };
}

function draw(save: BotUser, category: QuestCategory, rng: Rng): void {
  const pool = questDefs[category];
  const ids = randDistinct(rng, pool.length, QUESTS_PER_PERIOD)
    .map((i) => pool[i]?.id)
    .filter((id): id is number => id !== undefined);
  save.quests[category] = ids;
  save.quests.premium[category] = [
    { premium: 1, id: ids[ids.length - 3] ?? 0 },
    { premium: 2, id: ids[ids.length - 2] ?? 0 },
    { premium: 3, id: ids[ids.length - 1] ?? 0 },
  ];
}

/** A quest period has ended: the next action draws new quests (`questGenerator`). */
export function questsDue(save: BotUser, now: number): boolean {
  return now > dailyResetAt(save) || now > save.stats.quest.weekly.date;
}

/** Draws new quests when a period has ended (`questGenerator`). */
export function questGenerator(save: BotUser, now: number, rng: Rng): void {
  const premium = premiumOf(save, now).premium;
  if (now > dailyResetAt(save)) {
    save.stats.quest.daily = freshPeriod(save, now, premium);
    draw(save, 'daily', rng);
  }
  if (now > save.stats.quest.weekly.date) {
    save.stats.quest.weekly = freshPeriod(save, weeklyResetAfter(now), premium);
    draw(save, 'weekly', rng);
  }
}

/** Premium tier a quest needs (0 = none), from `quests.premium`. */
export function questPremiumTier(save: BotUser, category: QuestCategory, id: number): number {
  return save.quests.premium[category].find((q) => q.id === id)?.premium ?? 0;
}

/** A premium quest above the account's tier never completes (`checkQuestsDone`). */
export function isQuestLocked(save: BotUser, category: QuestCategory, id: number, now: number): boolean {
  return premiumOf(save, now).premium < questPremiumTier(save, category, id);
}

/** Goal of a quest as `checkQuestsDone` tests it. */
export function questTarget(save: BotUser, category: QuestCategory, q: QuestDef, premium: PremiumBoost): number {
  const s = save.stats.quest[category];
  const tierMul = q.premium ? premium.premium + 1 : 1;
  switch (q.type) {
    case 'chafer-bet':
    case 'bet-bet':
    case 'larva-bet':
      return Math.floor((q.data / 100) * s.money);
    case 'spot-bet':
      return Math.floor(10 + (q.data / 100) * s.lootbox);
    case 'bet-do':
    case 'toutou':
    case 'toutou-toutou':
    case 'toutou-golden':
    case 'toutou-dark':
    case 'toutou-diamond':
    case 'toutou-king':
    case 'boss':
      return q.data * tierMul;
    case 'mission': {
      const prem = premium.premium > 0 ? 1 + premium.mission.fakecoin / 100 : 1;
      return Math.floor(((100 * (1 + s.level / 10) * (1 + 150 / 100) * prem) / 6) * q.data * 1 - q.data / 50);
    }
    case 'items-characters-get':
    case 'items-weapons-get':
    case 'items-shields-get':
    case 'items-pets-get':
    case 'items-items-get':
      return 100 + q.data * s.level;
    case 'damages-dealt':
      return q.data * s.attack * (q.premium ? (PREMIUM_ADJUST[premium.premium] ?? 1) : 1);
    default:
      return q.data;
  }
}

/** Current counter for a quest (`checkQuestsAdvancement`). */
export function questProgress(save: BotUser, category: QuestCategory, q: QuestDef): number {
  const s = save.stats.quest[category];
  switch (q.type) {
    case 'chafer-do': return s.chafer.count;
    case 'chafer-bet': return s.chafer.bet;
    case 'bet-do': return s.bet.count;
    case 'bet-bet': return s.bet.bet;
    case 'toutou': return s.toutou.count;
    case 'toutou-toutou': return s.toutou.toutou;
    case 'toutou-golden': return s.toutou.golden;
    case 'toutou-dark': return s.toutou.dark;
    case 'toutou-diamond': return s.toutou.diamond;
    case 'toutou-king': return s.toutou.king;
    case 'boss': return s.boss.count;
    case 'mission': return s.mission.fakecoin;
    case 'reset': return s.reset.count;
    case 'adventures-normal': return s.adventures.normal;
    case 'adventures-hard': return s.adventures.hard;
    case 'adventures-nightmare': return s.adventures.nightmare;
    case 'adventures-kill': return s.adventures.kill;
    case 'spot-do': return s.spot.count;
    case 'spot-bet': return s.spot.bet;
    case 'larva-do': return s.larva.count;
    case 'larva-bet': return s.larva.bet;
    case 'items-characters-get': return s.items.characters;
    case 'items-weapons-get': return s.items.weapons;
    case 'items-shields-get': return s.items.shields;
    case 'items-pets-get': return s.items.pets;
    case 'items-items-get': return s.items.total;
    case 'items-legendary-get': return s.items.legendary;
    case 'items-characters-upgrade': return s.upgrade.characters;
    case 'items-weapons-upgrade': return s.upgrade.weapons;
    case 'items-shields-upgrade': return s.upgrade.shields;
    case 'items-pets-upgrade': return s.upgrade.pets;
    case 'items-items-upgrade': return s.upgrade.total;
    case 'battlepass-step': return s.battlepass.step;
    case 'damages-dealt': return s.damage.total;
    case 'pvp-attack': return s.pvp.total;
    case 'chest-open': return s.chests.total;
    case 'spend-fakecoin': return s.spend.fakecoin;
    case 'spend-lootcoin': return s.spend.lootcoin;
    case 'quest-do': return s.quests.total;
    case 'nugget-get': return s.nuggets.total;
    case 'raid-do': return s.raid.total;
    case 'reward-do': return s.reward.total;
    default: return 0;
  }
}

/** Pass XP paid by a quest (`questReward`): 135 daily, 540 weekly, halved for a weekly premium quest. */
export function questReward(save: BotUser, category: QuestCategory, id: number): number {
  if (category === 'daily') return 135;
  return save.quests.premium.weekly.some((q) => q.id === id) ? 270 : 540;
}

function bothPeriods(save: BotUser, fn: (p: QuestPeriod) => void): void {
  fn(save.stats.quest.daily);
  fn(save.stats.quest.weekly);
}

/** Moves the quest counters the bot moves for what just happened (both periods, as the bot). */
export function trackQuestEvents(save: BotUser, events: readonly GameEvent[]): void {
  for (const e of events) {
    switch (e.type) {
      case 'itemsSummoned': {
        const legendary = e.results.filter((r) => r.tier === 5).length;
        bothPeriods(save, (p) => {
          p.items[e.itemType] += e.amount;
          p.items.total += e.amount;
          p.items.legendary += legendary;
          p.spend.lootcoin += e.amount;
        });
        break;
      }
      case 'itemUpgraded':
        bothPeriods(save, (p) => {
          p.upgrade[e.itemType] += 1;
          p.upgrade.total += 1;
          p.spend.fakecoin += e.price;
        });
        break;
      case 'itemsUpgraded':
        bothPeriods(save, (p) => {
          p.upgrade[e.itemType] += e.upgrades;
          p.upgrade.total += e.upgrades;
          p.spend.fakecoin += e.spent;
        });
        break;
      case 'missionRewards':
        bothPeriods(save, (p) => {
          p.mission.count += 1;
          p.mission.fakecoin += e.money;
          p.mission.lootcoin += e.lootbox;
        });
        break;
      case 'dailyClaimed':
        bothPeriods(save, (p) => (p.reward.total += 1));
        break;
      case 'adventureCompleted':
        // `adventures/rewards.ts`: "N or more" counts every difficulty up to the one played; kills are a flat 3 per win (2 on chapter 1, step 1 — 0-based, K-11).
        bothPeriods(save, (p) => {
          p.damage.total += e.damage;
          p.adventures.kill += e.chapter === 1 && e.step === 1 ? 2 : 3;
          p.adventures.normal += 1;
          if (e.difficulty >= 1) p.adventures.hard += 1;
          if (e.difficulty >= 2) p.adventures.nightmare += 1;
        });
        break;
      case 'battlepassClaimed':
        bothPeriods(save, (p) => (p.battlepass.step += e.tiers));
        break;
      case 'dofusRolled':
        // `roulette.ts`: every Dofus won counts by tier, plus one per roll.
        bothPeriods(save, (p) => {
          p.toutou.count += 1;
          p.toutou.toutou += e.earned.one;
          p.toutou.golden += e.earned.two;
          p.toutou.dark += e.earned.three;
          p.toutou.diamond += e.earned.four;
          p.toutou.king += e.earned.five;
        });
        break;
      case 'betPlaced':
        // `shop/bet.ts#betting`: the stake counts as fakecoins spent, won or lost.
        bothPeriods(save, (p) => {
          p.bet.count += 1;
          p.bet.bet += e.amount;
          p.spend.fakecoin += e.amount;
        });
        break;
      case 'spotEnded':
        // A flagged cash-out writes nothing in the bot, quest counters included.
        if (!e.flagged)
          bothPeriods(save, (p) => {
            p.spot.count += 1;
            p.spot.bet += e.bid;
            p.spend.lootcoin += e.bid;
          });
        break;
    }
  }
}

/** Completes every quest whose goal is reached (`checkQuestsDone` + `updateQuest`). */
export function checkQuests(save: BotUser, now: number, events: GameEvent[]): void {
  const premium = premiumOf(save, now);
  for (const category of QUEST_CATEGORIES) {
    for (const id of [...save.quests[category]]) {
      const q = questDef(category, id);
      if (!q) continue;
      if (premium.premium < questPremiumTier(save, category, id)) continue;
      if (questProgress(save, category, q) < questTarget(save, category, q, premium)) continue;
      const reward = questReward(save, category, id);
      save.battlepass.xp += reward;
      save.stats.quest.count += 1;
      save.stats.quest.points += reward;
      const list = save.quests[category];
      const idx = list.indexOf(id);
      if (idx !== -1) list.splice(idx, 1);
      bothPeriods(save, (p) => (p.quests.total += 1));
      const level = battlepassLevelFor(save.battlepass.xp);
      if (level > save.battlepass.level) events.push({ type: 'battlepassLevelUp', from: save.battlepass.level, to: level });
      save.battlepass.level = level;
      events.push({ type: 'questCompleted', category, id, reward });
    }
  }
}
